import { storage } from '../services/firebase/core';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import logger from './logger';

/**
 * Compresses an image before uploading
 * 
 * @param file The image file to compress
 * @param maxWidth The maximum width of the compressed image
 * @param maxHeight The maximum height of the compressed image
 * @param quality The quality of the compressed image (0-1)
 * @returns A promise that resolves to a File object
 */
export const compressImage = (
  file: File,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Don't compress if not an image
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event: any) => {
      const img = new Image();
      img.src = event.target.result as string;
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get the compressed data URL
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not create blob'));
              return;
            }
            
            // Create a new file from the blob
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            
            resolve(newFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Error loading image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
  });
};

/**
 * Uploads a file to Firebase Storage
 * 
 * @param path The path in Firebase Storage to upload to
 * @param file The file to upload
 * @param compress Whether to compress the file before uploading (only applies to images)
 * @returns A promise that resolves to the download URL
 */
export const uploadToStorage = async (
  path: string,
  file: File,
  compress: boolean = true
): Promise<string> => {
  try {
    // Compress image if needed
    let fileToUpload = file;
    if (compress && file.type.startsWith('image/')) {
      fileToUpload = await compressImage(file);
    }
    
    // Upload file
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    logger.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Formats a Firebase error code into a user-friendly message
 * 
 * @param error The Firebase error object
 * @returns A user-friendly error message
 */
export const formatFirebaseError = (error: any): string => {
  if (!error) return 'An unknown error occurred';

  const code = error.code || '';
  
  // Auth errors
  if (code.startsWith('auth/')) {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password';
      case 'auth/email-already-in-use':
        return 'This email is already in use';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/invalid-email':
        return 'Please enter a valid email address';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method. Please sign in with your original method first, then link your accounts in Settings.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/requires-recent-login':
        return 'Please sign in again to complete this action';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/username-already-in-use':
        return 'This username is already taken';
      default:
        return error.message || 'An authentication error occurred';
    }
  }
  
  // Storage errors
  if (code.startsWith('storage/')) {
    switch (code) {
      case 'storage/object-not-found':
        return 'File not found';
      case 'storage/unauthorized':
        return 'You do not have permission to access this file';
      case 'storage/canceled':
        return 'File upload was canceled';
      case 'storage/retry-limit-exceeded':
        return 'Maximum retry attempts exceeded for file upload';
      case 'storage/invalid-checksum':
        return 'File upload failed: invalid checksum';
      case 'storage/quota-exceeded':
        return 'Storage quota exceeded';
      default:
        return error.message || 'A storage error occurred';
    }
  }
  
  // Database errors
  if (code.startsWith('database/')) {
    switch (code) {
      case 'database/permission-denied':
        return 'You do not have permission to perform this action';
      case 'database/unavailable':
        return 'The database is currently unavailable';
      default:
        return error.message || 'A database error occurred';
    }
  }
  
  // Generic error fallback
  return error.message || 'An error occurred';
};

/**
 * Generates a unique ID for Firebase
 * 
 * @returns A string containing a timestamp and random characters
 */
export const generateUniqueId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomStr}`;
};

/**
 * Generates a unique ID for Firebase documents
 * 
 * @returns A string containing a timestamp and random characters
 */
export const generateId = (): string => {
  return generateUniqueId();
};

/**
 * Extracts a storage path from a Firebase storage URL
 * 
 * @param url The Firebase storage URL
 * @returns The storage path
 */
export const getStoragePathFromUrl = (url: string): string => {
  try {
    const decodedUrl = decodeURIComponent(url);
    const path = decodedUrl.split('/o/')[1]?.split('?')[0];
    return path || '';
  } catch (error) {
    logger.error('Error extracting storage path:', error);
    return '';
  }
};

/**
 * Creates a timestamp for Firebase documents
 * @returns {number} Current timestamp in milliseconds
 */
export const createTimestamp = (): number => {
  return Date.now();
};

/**
 * Sanitizes a string to be used as a Firebase key
 * Firebase keys cannot contain: . # $ / [ ]
 * Also replaces spaces with underscores and converts to lowercase
 *
 * @param str The string to sanitize
 * @returns A string safe to use as a Firebase key
 */
export const sanitizeFirebaseKey = (str: string): string => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[.#$/[\]]/g, '_')  // Replace Firebase-invalid chars with underscore
    .replace(/\s+/g, '_')           // Replace spaces with underscore
    .replace(/_+/g, '_')            // Collapse multiple underscores
    .replace(/^_|_$/g, '');         // Remove leading/trailing underscores
};

/**
 * Generates a technique ID from a technique name
 * Sanitizes for Firebase key requirements
 *
 * @param techName The technique name
 * @returns A sanitized technique ID
 */
export const generateTechniqueId = (techName: string): string => {
  return sanitizeFirebaseKey(techName);
};